import { VBox } from "@ui/box/Box";
import { ChevronDown, Landmark } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GD } from "src/GD";
import { IClient } from "src/services/ClientService";
import { PeriodSelector } from "src/view/statView/PeriodSelector";
import { RollingReserveHeldCards } from "src/view/statView/RollingReserveHeldCards";
import { RollingReservePeriodCard } from "src/view/statView/RollingReservePeriodCard";
import { ClientEarningsCalmCard } from "./ClientEarningsCalmCard";
import { ClientEarningsCard } from "./ClientEarningsCard";
import { ClientStatementHealthCard } from "./ClientStatementHealthCard";
import styled from "styled-components";
import { statFilterSurface } from "src/view/statView/panelStyles";
import { selectSharedPreset, selectSharedRange, useSharedPeriod } from "src/view/statView/sharedPeriod";

const RollingGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	min-width: 0;
	@media (max-width: 1050px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
	@media (max-width: 680px) { grid-template-columns: minmax(0, 1fr); }
`;

const RollingDetails = styled.details`
	min-width: 0;
	> summary { display: flex; align-items: center; gap: 10px; padding: 16px 20px; border: 1px solid #d0d5dd; border-radius: 12px; background: #fff; box-shadow: 0 1px 2px rgba(16,24,40,.05); color: #344054; font-size: 13px; font-weight: 600; list-style: none; cursor: pointer; }
	> summary::-webkit-details-marker { display: none; }
	> summary:focus-visible { outline: 2px solid #6374d9; outline-offset: 3px; }
	> summary .chevron { margin-left: auto; }
	&[open] > summary .chevron { transform: rotate(180deg); }
	> div { padding-top: 12px; }
`;

// The alternative folds auxiliary financial blocks; the current layout remains the default.
const LAYOUT_KEY = "clientEarnings.layout";
const readLayout = () => { try { return localStorage.getItem(LAYOUT_KEY) === "calm" ? "calm" : "classic"; } catch { return "classic"; } };

const LayoutSwitch = styled.div`
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 10px;
	color: #667085;
	font-size: 12px;
	.options { display: inline-flex; padding: 2px; border: 1px solid #d0d5dd; border-radius: 8px; background: #fff; }
	button { padding: 4px 10px; border: 0; border-radius: 6px; background: transparent; color: #667085; font: inherit; font-size: 12px; cursor: pointer; }
	button[aria-pressed="true"] { background: #edf4fa; color: #182230; font-weight: 600; }
	button:focus-visible { outline: 2px solid #6374d9; outline-offset: 1px; }
`;

const formatDate = (date: Date) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const periodBoundary = (index: number, boundary: "from" | "to") => {
	const now = new Date();
	if (index === 0) return formatDate(now);
	if (index === 1) { now.setDate(now.getDate() - 1); return formatDate(now); }
	if (index === 2) { if (boundary === "from") now.setDate(now.getDate() - 6); return formatDate(now); }
	if (index === 3) { if (boundary === "from") now.setDate(now.getDate() - 29); return formatDate(now); }
	if (index === 4) return formatDate(boundary === "from" ? new Date(now.getFullYear(), now.getMonth(), 1) : now);
	return formatDate(boundary === "from" ? new Date(now.getFullYear(), now.getMonth() - 1, 1) : new Date(now.getFullYear(), now.getMonth(), 0));
};

export const ClientAccountantView = ({ client }: { client: IClient }) => {
	const { t } = useTranslation();
	const [project] = GD.B_SELECTED_PROJECT.use();
	const [user] = GD.REQ_PROFILE.useValue(undefined, undefined, [project]);
	const { selectedDate, manualRange } = useSharedPeriod();
	const dates = [t("dashboard.periods.today"), t("dashboard.periods.yesterday"), t("dashboard.periods.last7Days"), t("dashboard.periods.last30Days"), t("dashboard.periods.thisMonth"), t("dashboard.periods.lastMonth")];
	const from = manualRange?.from ?? periodBoundary(selectedDate, "from");
	const to = manualRange?.to ?? periodBoundary(selectedDate, "to");
	const [layout, setLayout] = useState<"classic" | "calm">(readLayout);
	const chooseLayout = (next: "classic" | "calm") => {
		setLayout(next);
		try { localStorage.setItem(LAYOUT_KEY, next); } catch { /* the choice just is not remembered */ }
	};
	const EarningsCard = layout === "calm" ? ClientEarningsCalmCard : ClientEarningsCard;
	const rollingCards = project && user && <RollingGrid>
		<RollingReserveHeldCards key={`${client.id}:${project.id}:${user.id}`} clientId={client.id} projectId={project.id} userId={user.id} />
		<RollingReservePeriodCard key={`${client.id}:${project.id}:${user.id}:${from}:${to}`} clientId={client.id} projectId={project.id} userId={user.id} from={from} to={to} />
	</RollingGrid>;

	return <VBox sx={{ gap: "20px", minWidth: 0 }}>
		{project && user && <ClientStatementHealthCard key={`${client.id}:${project.id}:${user.id}`} clientId={client.id} projectId={project.id} userId={user.id} compact={layout === "calm"} />}
		<PeriodSelector style={statFilterSurface} dates={dates} selectedDate={manualRange ? -1 : selectedDate} onDateChange={selectSharedPreset}
			from={from} to={to} onRangeChange={selectSharedRange}
			selectedCurrency="EUR" onCurrencyChange={() => undefined} channels={[]} channelsBusy={false}
			selectedChannel="" onChannelChange={() => undefined} showCurrency={false} />
		{project && user && <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
			{layout === "calm" ? <RollingDetails>
				<summary><Landmark size={18} aria-hidden="true" />{t("accountant.calm.reserveDetails")}<ChevronDown className="chevron" size={18} aria-hidden="true" /></summary>
				<div>{rollingCards}</div>
			</RollingDetails> : rollingCards}
			<LayoutSwitch>
				<span>{t("accountant.calm.switchLabel")}</span>
				<div className="options" role="group" aria-label={t("accountant.calm.switchLabel")}>
					{(["classic", "calm"] as const).map(option => <button key={option} type="button" aria-pressed={layout === option} onClick={() => chooseLayout(option)}>
						{t(option === "calm" ? "accountant.calm.switchCalm" : "accountant.calm.switchClassic")}
					</button>)}
				</div>
			</LayoutSwitch>
			<EarningsCard clientId={client.id} clientName={client.name} projectId={project.id} userId={user.id} from={from} to={to} />
		</div>}
	</VBox>;
};
